/**
 * Primary Ledger Snapshots - Period Calculation Utilities
 * 
 * Functions for calculating and navigating time periods (daily/weekly/monthly)
 * with proper handling of week boundaries (Sunday-Saturday) and date transitions.
 */

import { DatePeriod, PeriodType } from '@/types/primaryLedgerSnapshots';

/**
 * Get the day of week (0 = Sunday, 6 = Saturday)
 */
function getDayOfWeek(date: Date): number {
  return date.getDay();
}

/**
 * Get the start of day (midnight) for a given date
 */
function getStartOfDay(date: Date): Date {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  return start;
}

/**
 * Get the end of day (23:59:59.999) for a given date
 */
function getEndOfDay(date: Date): Date {
  const end = new Date(date);
  end.setHours(23, 59, 59, 999);
  return end;
}

/**
 * Add days to a date
 */
function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/**
 * Add months to a date
 */
function addMonths(date: Date, months: number): Date {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
}

/**
 * Format a single day label (e.g., "Mon, Jan 15, 2024")
 */
export function formatDayLabel(date: Date): string {
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

/**
 * Format a week label handling month/year transitions
 * Examples: 
 * - "Jan 7-13, 2024" (same month)
 * - "Dec 31, 2023 - Jan 6, 2024" (year transition)
 * - "Jan 28 - Feb 3, 2024" (month transition)
 */
export function formatWeekLabel(startDate: Date, endDate: Date): string {
  const startMonth = startDate.toLocaleDateString('en-US', { month: 'short' });
  const endMonth = endDate.toLocaleDateString('en-US', { month: 'short' });
  const startYear = startDate.getFullYear();
  const endYear = endDate.getFullYear();
  const startDay = startDate.getDate();
  const endDay = endDate.getDate();

  if (startYear !== endYear) {
    return `${startMonth} ${startDay}, ${startYear} - ${endMonth} ${endDay}, ${endYear}`;
  } else if (startMonth !== endMonth) {
    return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${endYear}`;
  } else {
    return `${startMonth} ${startDay}-${endDay}, ${startYear}`;
  }
}

/**
 * Format a month label (e.g., "January 2024")
 */
export function formatMonthLabel(date: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric'
  });
}

/**
 * Get the last complete week (Sunday-Saturday)
 * If today is Sunday-Friday, returns the previous complete week
 * If today is Saturday, returns the week ending today
 */
export function getLastCompleteWeek(): DatePeriod {
  const today = new Date();
  const dayOfWeek = getDayOfWeek(today);
  
  let endDate: Date;
  if (dayOfWeek === 6) {
    endDate = getEndOfDay(today);
  } else {
    const daysToLastSaturday = dayOfWeek + 1;
    endDate = getEndOfDay(addDays(today, -daysToLastSaturday));
  }
  
  const startDate = getStartOfDay(addDays(endDate, -6));
  
  return {
    type: 'weekly',
    startDate,
    endDate,
    label: formatWeekLabel(startDate, endDate)
  };
}

/**
 * Get the current week containing today (Sunday-Saturday)
 * Note: This may be an incomplete week if today is not Saturday
 */
export function getCurrentWeek(): DatePeriod {
  const today = new Date();
  const dayOfWeek = getDayOfWeek(today);
  
  const startDate = getStartOfDay(addDays(today, -dayOfWeek));
  const endDate = getEndOfDay(addDays(startDate, 6));
  
  return {
    type: 'weekly',
    startDate,
    endDate,
    label: formatWeekLabel(startDate, endDate)
  };
}

/**
 * Navigate to the next or previous period based on period type
 * @param currentPeriod The current period
 * @param direction 'next' or 'prev'
 */
export function navigatePeriod(
  currentPeriod: DatePeriod,
  direction: 'next' | 'prev'
): DatePeriod {
  const multiplier = direction === 'next' ? 1 : -1;
  
  switch (currentPeriod.type) {
    case 'daily': {
      const newDate = addDays(currentPeriod.startDate, multiplier);
      return {
        type: 'daily',
        startDate: getStartOfDay(newDate),
        endDate: getEndOfDay(newDate),
        label: formatDayLabel(newDate)
      };
    }
    
    case 'weekly': {
      const newStartDate = addDays(currentPeriod.startDate, 7 * multiplier);
      const newEndDate = addDays(newStartDate, 6);
      return {
        type: 'weekly',
        startDate: getStartOfDay(newStartDate),
        endDate: getEndOfDay(newEndDate),
        label: formatWeekLabel(newStartDate, newEndDate)
      };
    }
    
    case 'monthly': {
      const newDate = addMonths(currentPeriod.startDate, multiplier);
      const year = newDate.getFullYear();
      const month = newDate.getMonth();
      const startDate = new Date(year, month, 1);
      const endDate = new Date(year, month + 1, 0);
      
      return {
        type: 'monthly',
        startDate: getStartOfDay(startDate),
        endDate: getEndOfDay(endDate),
        label: formatMonthLabel(startDate)
      };
    }
  }
}

/**
 * Get the previous period for comparison
 * @param currentPeriod The current period to compare against
 */
export function getPreviousPeriod(currentPeriod: DatePeriod): DatePeriod {
  return navigatePeriod(currentPeriod, 'prev');
}

/**
 * Calculate the period containing a given date
 * @param date The date to calculate the period for
 * @param type The type of period to calculate
 */
export function calculatePeriodFromDate(date: Date, type: PeriodType): DatePeriod {
  switch (type) {
    case 'daily': {
      return {
        type: 'daily',
        startDate: getStartOfDay(date),
        endDate: getEndOfDay(date),
        label: formatDayLabel(date)
      };
    }
    
    case 'weekly': {
      const dayOfWeek = getDayOfWeek(date);
      const startDate = getStartOfDay(addDays(date, -dayOfWeek));
      const endDate = getEndOfDay(addDays(startDate, 6));
      
      return {
        type: 'weekly',
        startDate,
        endDate,
        label: formatWeekLabel(startDate, endDate)
      };
    }
    
    case 'monthly': {
      const year = date.getFullYear();
      const month = date.getMonth();
      const startDate = new Date(year, month, 1);
      const endDate = new Date(year, month + 1, 0);
      
      return {
        type: 'monthly',
        startDate: getStartOfDay(startDate),
        endDate: getEndOfDay(endDate),
        label: formatMonthLabel(startDate)
      };
    }
  }
}